import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { setCurrentAccount } from '../../lib/auth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '../ui/tooltip';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import {
  Users, UserPlus, Loader2, Search, ExternalLink, Sparkles, RefreshCw,
  Globe, MonitorSmartphone, Clock, Repeat, FileText, Coins, TrendingDown,
} from 'lucide-react';

const RANGES = [
  { v: '30', l: 'Last 30 days' },
  { v: '90', l: 'Last 90 days' },
  { v: '180', l: 'Last 180 days' },
  { v: '365', l: 'Last 12 months' },
  { v: '3650', l: 'All time' },
];

const fmtDur = (s) => {
  s = Number(s || 0);
  if (!s) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${Math.round(s % 60)}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return '';
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

function Funnel({ days }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/super-admin/crm-backend/funnel', { params: { days } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);
  if (!data) return null;
  const chart = [
    { stage: 'Trials', value: data.trials, fill: '#6366f1' },
    { stage: 'Registrations', value: data.registrations, fill: '#0ea5e9' },
    { stage: 'Subscriptions', value: data.subscriptions, fill: '#10b981' },
  ];
  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="crm-funnel-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-foreground">Conversion Funnel</h3>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span data-testid="crm-funnel-conv-reg">Trial→Reg <b className="text-foreground">{data.conv_trial_to_reg}%</b></span>
          <span data-testid="crm-funnel-conv-sub">Reg→Sub <b className="text-foreground">{data.conv_reg_to_sub}%</b></span>
        </div>
      </div>
      <div style={{ width: '100%', height: 160 }}>
        <ResponsiveContainer>
          <BarChart data={chart} margin={{ top: 18, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.25} />
            <XAxis dataKey="stage" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={64} minPointSize={2}>
              {chart.map((e, i) => <Cell key={i} fill={e.fill} />)}
              <LabelList dataKey="value" position="top" style={{ fontSize: 13, fontWeight: 700 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CrmTable({ kind, days }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [enriching, setEnriching] = useState('');

  const endpoint = kind === 'trial'
    ? '/super-admin/crm-backend/trials'
    : '/super-admin/crm-backend/free-registrations';

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params: { days, search: q ?? search } });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [endpoint, days, search]);

  useEffect(() => { load(''); /* eslint-disable-next-line */ }, [endpoint, days]);

  const gotoOrg = (row) => {
    if (!row.account_id) { toast.error('No org linked to this user'); return; }
    setCurrentAccount({
      id: row.account_id,
      organisation_name: row.org_name || row.org_identified || 'Trial Org',
      account_no: row.account_no || '',
      trial_mode: kind === 'trial',
    });
    toast.success(`Opening ${row.org_name || row.email}'s workspace…`);
    navigate('/bill-jobs');
  };

  const enrich = async (row) => {
    setEnriching(row.user_id);
    try {
      const { data } = await api.post('/super-admin/crm-backend/enrich-org', {
        account_id: row.account_id, email: row.email,
      });
      const en = data.org_enrichment || {};
      setRows((prev) => prev.map((r) => r.user_id === row.user_id
        ? { ...r, org_identified: en.company_name || r.org_identified, org_industry: en.industry || '', org_enriched: true }
        : r));
      toast.success(`Identified: ${en.company_name || '—'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI enrichment failed');
    } finally {
      setEnriching('');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="crm-search-input"
            className="pl-8 h-9"
            placeholder="Search name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(search); }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => load(search)} data-testid="crm-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
        <span className="text-xs text-muted-foreground ml-auto" data-testid="crm-rows-count-badge">
          {rows.length} {kind === 'trial' ? 'trial users' : 'free registrations'}
        </span>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-3 py-2.5">User</th>
                <th className="text-left font-medium px-3 py-2.5">Org Identified</th>
                <th className="text-center font-medium px-3 py-2.5">Bills</th>
                <th className="text-center font-medium px-3 py-2.5">Parse Status</th>
                <th className="text-right font-medium px-3 py-2.5">Credits</th>
                <th className="text-left font-medium px-3 py-2.5">Location / Device</th>
                <th className="text-center font-medium px-3 py-2.5">Engagement</th>
                <th className="text-center font-medium px-3 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">
                  No {kind === 'trial' ? 'trial users' : 'free registrations'} in this window.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-muted/30 align-top" data-testid={`crm-row-${r.user_id}`}>
                  {/* User */}
                  <td className="px-3 py-3">
                    <div className="font-medium text-foreground">{r.name || '—'}</div>
                    <div className="text-xs text-muted-foreground break-all">{r.email}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Joined {fmtDate(r.created_at)}</div>
                  </td>
                  {/* Org Identified */}
                  <td className="px-3 py-3">
                    <div className="flex items-start gap-1.5">
                      <div>
                        <div className="font-medium text-foreground">{r.org_identified || '—'}</div>
                        {r.org_industry && <div className="text-[11px] text-muted-foreground">{r.org_industry}</div>}
                        {r.org_domain && <div className="text-[11px] text-sky-600 dark:text-sky-400">{r.org_domain}</div>}
                      </div>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      className="h-6 px-1.5 mt-1 text-[11px] text-indigo-600 dark:text-indigo-400"
                      disabled={enriching === r.user_id}
                      onClick={() => enrich(r)}
                      data-testid={`crm-enrich-${r.user_id}`}
                    >
                      {enriching === r.user_id
                        ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        : <Sparkles className="h-3 w-3 mr-1" />}
                      {r.org_enriched ? 'Re-identify (AI)' : 'Identify (AI)'}
                    </Button>
                  </td>
                  {/* Bills */}
                  <td className="px-3 py-3 text-center">
                    <div className="inline-flex items-center gap-1 font-semibold text-foreground">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />{r.bills_uploaded}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{r.bills_parsed} loaded</div>
                  </td>
                  {/* Parse Status */}
                  <td className="px-3 py-3 text-center">
                    <div className="flex flex-col items-center gap-0.5 text-[11px]">
                      {r.bills_parsed > 0 && <span className="text-emerald-600 dark:text-emerald-400">✓ {r.bills_parsed} parsed</span>}
                      {r.parse_processing > 0 && <span className="text-amber-600 dark:text-amber-400">⏳ {r.parse_processing} processing</span>}
                      {r.parse_failed > 0 && <span className="text-rose-600 dark:text-rose-400">✕ {r.parse_failed} failed</span>}
                      {r.bills_uploaded === 0 && <span className="text-muted-foreground">No bills</span>}
                    </div>
                  </td>
                  {/* Credits */}
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-1 font-semibold text-foreground">
                      <Coins className="h-3.5 w-3.5 text-amber-500" />{Number(r.balance_credits || 0).toFixed(0)}
                    </div>
                  </td>
                  {/* Location / Device */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5 text-foreground">
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{flagEmoji(r.country_code)} {r.country || (r.ip ? 'Unknown' : '—')}</span>
                    </div>
                    {(r.city || r.region) && (
                      <div className="text-[11px] text-muted-foreground pl-5">{[r.city, r.region].filter(Boolean).join(', ')}</div>
                    )}
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-1">
                      <MonitorSmartphone className="h-3.5 w-3.5" />{r.device || '—'}
                    </div>
                    {r.ip && <div className="text-[10px] text-muted-foreground/70 pl-5 font-mono">{r.ip}</div>}
                  </td>
                  {/* Engagement */}
                  <td className="px-3 py-3 text-center">
                    <div className="inline-flex items-center gap-1 text-foreground" title="Times accessed / logged back in">
                      <Repeat className="h-3.5 w-3.5 text-muted-foreground" />{r.access_count}×
                    </div>
                    <div className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground mt-0.5" title="Avg active session time">
                      <Clock className="h-3 w-3" />{fmtDur(r.avg_active_seconds)}
                    </div>
                    <div className="text-[10px] text-muted-foreground/70 mt-0.5">last {fmtDate(r.last_active)}</div>
                  </td>
                  {/* Action */}
                  <td className="px-3 py-3 text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm" variant="outline"
                            className="h-8"
                            onClick={() => gotoOrg(r)}
                            data-testid={`crm-goto-org-${r.user_id}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Goto Org
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Open this user's workspace to see the bills they uploaded
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function BackendCrmTab() {
  const [subTab, setSubTab] = useState('trial');
  const [days, setDays] = useState('90');

  return (
    <div className="space-y-4" data-testid="backend-crm-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-indigo-500" /> Backend CRM
          </h2>
          <p className="text-sm text-muted-foreground">Trial &amp; free-plan funnel — engagement, geo, and org intelligence.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[170px] h-9" data-testid="crm-range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Funnel days={days} />

      {/* Sub-tabs: Trial | Free Registrations */}
      <div className="flex gap-1 border-b border-border">
        <button
          data-testid="crm-subtab-trial"
          onClick={() => setSubTab('trial')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
            subTab === 'trial'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="h-4 w-4" /> Trial
        </button>
        <button
          data-testid="crm-subtab-free"
          onClick={() => setSubTab('free')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
            subTab === 'free'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <UserPlus className="h-4 w-4" /> Free Registrations
        </button>
      </div>

      {subTab === 'trial' ? <CrmTable kind="trial" days={days} /> : <CrmTable kind="free" days={days} />}
    </div>
  );
}
